import { z } from 'zod';
/** Governed tag categories (Advanced Views spec §7.2). */
export const TagCategory = z.enum([
    'customer', 'component', 'product_area', 'severity', 'workflow',
    'technical', 'support', 'release', 'compliance', 'ai', 'custom',
]);
export const TagScope = z.enum(['workspace', 'project', 'team', 'ticket_type']);
/**
 * Normalize a tag name to PascalCase — the app-wide tag convention. Splits on
 * separators (space/underscore/hyphen/etc.) and camelCase boundaries, then
 * capitalizes each word and joins. e.g. "no_fault_found" → "NoFaultFound",
 * "support" → "Support", "Integration" → "Integration". Idempotent.
 */
export function toPascalCaseTag(name) {
    const words = name
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // split camelCase boundaries
        .split(/[^A-Za-z0-9]+/) // split on separators
        .filter(Boolean);
    const pascal = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
    return pascal || name.trim();
}
/** A master tag/label. */
export const Tag = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    description: z.string().nullable(),
    color: z.string(),
    category: z.string().nullable(),
    scope: z.string(),
    restricted: z.boolean(),
    active: z.boolean(),
    usageCount: z.number(),
    aliases: z.array(z.string()).default([]),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Lightweight tag reference embedded on a ticket projection. */
export const TagRef = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    color: z.string(),
    category: z.string().nullable(),
});
export const CreateTagInput = z.object({
    // Tag names are enforced PascalCase app-wide (create, rename, and the "+" flow).
    name: z.string().min(1).max(60).transform(toPascalCaseTag),
    description: z.string().optional(),
    color: z.string().optional(),
    category: TagCategory.optional(),
    scope: TagScope.optional(),
    restricted: z.boolean().optional(),
    aliases: z.array(z.string()).optional(),
});
export const UpdateTagInput = CreateTagInput.partial().extend({
    active: z.boolean().optional(),
});
/** Merge one or more source tags into a target tag (re-points all references). */
export const MergeTagsInput = z.object({
    sourceTagIds: z.array(z.string()).min(1),
    targetTagId: z.string(),
});
/** Full-replace the tag set on a single ticket. */
export const SetTicketTagsInput = z.object({ tagIds: z.array(z.string()) });
/** Add/remove tags across many tickets. */
export const BulkTagInput = z.object({
    ticketIds: z.array(z.string()).min(1).max(500),
    addTagIds: z.array(z.string()).optional(),
    removeTagIds: z.array(z.string()).optional(),
});
/** One row of the label→tag migration preview. */
export const TagMigrationCandidate = z.object({
    normalized: z.string(),
    suggestedName: z.string(),
    variants: z.array(z.string()),
    ticketCount: z.number(),
    existingTagId: z.string().nullable(),
});
//# sourceMappingURL=tag.js.map